import { growthStageAsset, image } from "./assets/manifest";

const GROWTH_THEME_IDS = [
  "lamb",
  "deer",
  "rabbit",
  "cat",
  "dog",
  "bear",
  "penguin",
  "bird",
  "fox",
  "squirrel",
  "dolphin",
  "pony",
  "pumpkin",
  "tree",
  "mushroom",
  "bamboo",
  "sunflower",
  "lotus",
  "pine",
  "strawberry",
  "cherry",
  "cactus",
  "succulent",
  "wheat"
] as const;

const GROWTH_THEME_NAMES: Record<(typeof GROWTH_THEME_IDS)[number], string> = {
  lamb: "小羊",
  deer: "小鹿",
  rabbit: "小兔",
  cat: "小猫",
  dog: "小狗",
  bear: "小熊",
  penguin: "企鹅",
  bird: "小鸟",
  fox: "小狐狸",
  squirrel: "小松鼠",
  dolphin: "小海豚",
  pony: "小马",
  pumpkin: "南瓜藤",
  tree: "花树",
  mushroom: "蘑菇",
  bamboo: "竹子",
  sunflower: "向日葵",
  lotus: "荷花",
  pine: "松树",
  strawberry: "草莓",
  cherry: "樱桃树",
  cactus: "仙人掌",
  succulent: "多肉",
  wheat: "小麦"
};

const GROWTH_STAGE_LABELS = ["萌芽期", "成长期", "进阶期", "荣誉期"];
const GROWTH_STAGE_LIMITS = [30, 70, 120];
const GROWTH_STAGE_FALLBACKS = [
  image.growthPot,
  image.growthFlower,
  image.growthFlower,
  image.growth
];

export { portraitSrc } from "./assets/media";

function growthStagePaths(id: string) {
  return [1, 2, 3, 4].map((stage) => growthStageAsset(id, stage));
}

export const GROWTH_THEMES = GROWTH_THEME_IDS.map((id) => ({
  id,
  name: GROWTH_THEME_NAMES[id],
  stages: growthStagePaths(id)
}));

export function normalizedGrowthLimits(growthLimits?: number[] | null) {
  const limits = Array.isArray(growthLimits) ? growthLimits.map(Number) : [];
  if (
    limits.length === 3 &&
    limits.every((item) => Number.isFinite(item) && item > 0) &&
    limits[0] < limits[1] &&
    limits[1] < limits[2]
  ) {
    return limits;
  }
  return GROWTH_STAGE_LIMITS;
}

function defaultGrowthTheme(student: { id?: number | null }) {
  const id = Number(student?.id || 1);
  const index = Number.isFinite(id) ? Math.max(0, Math.abs(Math.trunc(id)) - 1) % GROWTH_THEMES.length : 0;
  return GROWTH_THEMES[index];
}

function growthThemeFor(student: { id?: number | null; species?: string | null }) {
  return GROWTH_THEMES.find((item) => item.id === student.species) || defaultGrowthTheme(student);
}

function growthStage(points: number, growthLimits?: number[] | null) {
  const limits = normalizedGrowthLimits(growthLimits);
  if (points <= limits[0]) return 0;
  if (points <= limits[1]) return 1;
  if (points <= limits[2]) return 2;
  return 3;
}

function growthProgress(points: number, stage: number, growthLimits?: number[] | null) {
  const limits = normalizedGrowthLimits(growthLimits);
  const ranges = [
    [0, limits[0]],
    [limits[0], limits[1]],
    [limits[1], limits[2]],
    [limits[2], Math.max(limits[2] + 60, Math.round(limits[2] * 1.5))]
  ];
  const [min, max] = ranges[stage] || ranges[0];
  const percent = ((Math.max(points, min) - min) / Math.max(1, max - min)) * 100;
  return Math.max(0, Math.min(100, Math.round(percent)));
}

export function growthInfo(
  student: { id?: number | null; points?: number; species?: string | null },
  growthLimits?: number[] | null
) {
  const points = Number(student.points || 0);
  const theme = growthThemeFor(student);
  const stage = growthStage(points, growthLimits);
  return {
    imageSrc: theme.stages[stage] || GROWTH_STAGE_FALLBACKS[stage] || GROWTH_STAGE_FALLBACKS[0],
    label: `${theme.name}·${GROWTH_STAGE_LABELS[stage]}`,
    themeName: theme.name,
    themeId: theme.id,
    stage,
    progress: growthProgress(points, stage, growthLimits)
  };
}

/** Prefer growth-stage art as the student face so avatars evolve with points. */
export function studentFaceSrc(
  student: { id?: number | null; points?: number; species?: string | null },
  growthLimits?: number[] | null
) {
  return growthInfo(student, growthLimits).imageSrc;
}
